import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { availableWorkspaces, clearWorkspace } from '../utils/workspace';
import { activeHubKey, visibleHubs } from '../utils/hubs/registry';
import { searchLinks } from '../utils/hubs/search';
import { useAuth } from '../context/AuthContext';
import QRLookupModal from './QRLookupModal';
import ThemePicker from './ThemePicker';
import EnvironmentBadge from './EnvironmentBadge';
import ViewAsDialog from './ViewAsDialog';
import ChatButton from './chat/ChatButton';
import AiCreditsBadge from './credits/AiCreditsBadge';
import ChatDrawer, { ChatDrawerContext, useChatDrawer } from './chat/ChatDrawer';
import ReportIssueModal from './issues/ReportIssueModal';
import { useIssueReport } from './issues/IssueReportProvider';
import { FlagIcon } from './issues/ReportIssueButton';
import api from '../utils/api';
import { can } from '../utils/permissions';
// By file, not through ./organic's index: the index re-exports the hero,
// and anything imported through it rides in the one eager bundle.
import SectionTransition from './organic/SectionTransition.jsx';
import { srOnly } from './organic/motion';
import { intakeNav, specimenNav } from '../utils/linkedRecordNav';
import HubPager from './hubs/HubPager';
import useHubChain from '../hooks/useHubChain';
import useViewportGestures from '../hooks/useViewportGestures';
import { anyModalOpen } from './ui/useModal';
import { STROKE } from '../utils/gestures/recognise';
import { swipeOwner } from '../utils/gestures/arbitrate';
import { stopLabel, takeArriving } from '../utils/hubs/chain';

/* The page swipe between hubs (D12): stricter than the camera's -- a third of
 * the width, straight, within 30 degrees -- so reading and scrolling a page
 * never moves it by accident. */
const PAGE_SWIPE = Object.freeze({ ...STROKE, swipeTravel: 0.3 });

/* Whether the bar is the phone's (the ☰ menu holds its actions): the
 * breakpoint of `.pn-desktop` below. */
const phoneBar = () => typeof window !== 'undefined' && !!window.matchMedia?.('(max-width: 900px)')?.matches;

/* ── Icons ────────────────────────────────────────────────────────────────
 * Inline SVG in currentColor, so they follow the bar's text colour in every
 * theme. Emoji rendered at a different size and baseline on every platform,
 * which is part of what made the old bar look crowded. */
const Svg = ({ children }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    {children}
  </svg>
);
const SearchIcon = () => <Svg><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></Svg>;
const UserIcon = () => <Svg><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" /></Svg>;
const GearIcon = () => (
  <Svg>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1.08 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </Svg>
);
const Chevron = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false">
    <path d="M1.5 3.5 5 7l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

/* Closes whatever `ref` wraps when a pointer lands outside it, or on Escape. */
function useDismiss(open, ref, onClose) {
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, ref, onClose]);
}

const isCurrent = (pathname, to) => pathname === to || pathname.startsWith(`${to}/`);

/* One hub's menu (utils/hubs/registry.js). The trigger shows the hub's short
 * name and is marked current when the page you are on lives inside the hub;
 * the menu opens with the hub's own page, then each section under its
 * heading. */
function HubMenu({ hub, title, short, tip, active, open, onToggle, onClose, t, extraFor, pathname }) {
  const ref = useRef(null);
  useDismiss(open, ref, onClose);
  const menuId = `nav-hub-${hub.key}`;
  const hubCurrent = pathname === hub.path;
  return (
    <div ref={ref} className="pn-group">
      <button
        type="button"
        className={`pn-group-trigger${active ? ' is-active' : ''}${open ? ' is-open' : ''}`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={menuId}
        title={tip}
        data-hub={hub.key}
        onClick={onToggle}
      >
        <span>{short}</span>
        <Chevron />
      </button>
      {open && (
        <div id={menuId} className="pn-menu" role="menu" aria-label={title}>
          <Link
            to={hub.path}
            role="menuitem"
            className={`pn-menu-item pn-menu-hub${hubCurrent ? ' is-current' : ''}`}
            aria-current={hubCurrent ? 'page' : undefined}
            onClick={onClose}
          >
            {t('hubs:openHub', { hub: title })}
          </Link>
          {hub.sections.map(section => {
            const headingId = `${menuId}-${section.key}`;
            return (
              <div key={section.key} role="group" aria-labelledby={headingId} className="pn-menu-section">
                <div id={headingId} role="presentation" className="pn-menu-heading">{t(section.label)}</div>
                {section.items.map(item => {
                  const current = isCurrent(pathname, item.to);
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      role="menuitem"
                      className={`pn-menu-item${current ? ' is-current' : ''}`}
                      aria-current={current ? 'page' : undefined}
                      onClick={onClose}
                    >
                      {t(item.label)}
                      {extraFor?.(item)}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* The phone menu's links (≤ 900 px): a search box over every link the person
 * may follow, Home, then one collapsible section per hub, all closed at first.
 * While something is typed, the matches show flat, each with its hub. */
function PhoneHubs({ home, user, hubs, t, pathname, expanded, onToggle, query, onQuery, extraFor }) {
  const searchId = 'pn-mobile-search';
  const trimmed = query.trim();
  const found = trimmed ? searchLinks(user, query, t) : null;
  const hubTitle = (key) => t(hubs.find(h => h.key === key)?.label || '');
  const link = (item, extra) => {
    const current = isCurrent(pathname, item.to);
    return (
      <Link key={item.to} to={item.to} className={`pn-mobile-link${current ? ' is-current' : ''}`}
            aria-current={current ? 'page' : undefined}>
        {t(item.label)}
        {extra}
      </Link>
    );
  };
  return (
    <>
      <div className="pn-mobile-search" role="search">
        <label htmlFor={searchId} className="pn-mobile-title">{t('hubs:search.label')}</label>
        <input id={searchId} type="search" value={query} autoComplete="off" spellCheck={false}
          placeholder={t('hubs:search.placeholder')} onChange={(e) => onQuery(e.target.value)}
          data-testid="nav-search" />
      </div>
      {home}
      {found ? (
        <div className="pn-mobile-group" data-testid="nav-search-results" aria-label={t('hubs:search.results')}
             role="region">
          {found.length === 0 && (
            <p className="pn-mobile-none" role="status">{t('hubs:search.none', { query: trimmed })}</p>
          )}
          {found.map(({ hubKey, item }) => link(item, (
            <span className="pn-mobile-hubname">{hubTitle(hubKey)}</span>
          )))}
        </div>
      ) : hubs.map(hub => {
        const title = t(hub.label);
        const open = expanded.has(hub.key);
        const buttonId = `pn-acc-${hub.key}`;
        const regionId = `pn-acc-${hub.key}-panel`;
        const hubCurrent = pathname === hub.path;
        return (
          <div key={hub.key} className="pn-mobile-group">
            <button type="button" id={buttonId} className="pn-accordion" aria-expanded={open}
              aria-controls={regionId} title={t('hubs:tip.accordion', { hub: title })}
              data-hub={hub.key} onClick={() => onToggle(hub.key)}>
              <span>{title}</span>
              <Chevron />
            </button>
            {open && (
              <div id={regionId} role="region" aria-labelledby={buttonId}>
                <Link to={hub.path} className={`pn-mobile-link pn-mobile-hublink${hubCurrent ? ' is-current' : ''}`}
                      aria-current={hubCurrent ? 'page' : undefined}>
                  {t('hubs:openHub', { hub: title })}
                </Link>
                {hub.sections.map(section => (
                  <div key={section.key} role="group" aria-label={t(section.label)}>
                    <div className="pn-mobile-title" aria-hidden="true">{t(section.label)}</div>
                    {section.items.map(item => link(item, extraFor?.(item)))}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

/* An icon-only quick action. The tooltip is the label: it appears on hover and
 * on keyboard focus (a `title` attribute does neither reliably), and the same
 * text is the accessible name. */
const IconButton = React.forwardRef(function IconButton({ label, pressed, children, className = '', ...rest }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className={`pn-icon${pressed ? ' is-open' : ''} ${className}`.trim()}
      aria-label={label}
      data-tooltip={label}
      {...rest}
    >
      {children}
    </button>
  );
});

export default function Layout({ children }) {
  const { t, i18n } = useTranslation(['nav', 'common', 'hubs']);
  const { t: tWorkspace } = useTranslation('workspace');
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [lookupOpen, setLookupOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false); // username/language/logout dropdown
  const [openGroup, setOpenGroup]     = useState(null);  // key of the hub menu that is open
  const [phoneOpen, setPhoneOpen]     = useState(() => new Set()); // the phone menu's open hub sections
  const [phoneQuery, setPhoneQuery]   = useState('');    // the phone menu's quick search
  const [reportOpen, setReportOpen]   = useState(false); // the Report issue dialog, when no provider has one
  const sharedReport = useIssueReport();
  const openReport = () => (sharedReport ? sharedReport.openReport() : setReportOpen(true));
  const [openIssues, setOpenIssues]   = useState(null);  // open issue reports, read when the admin menu opens
  const [viewAsOpen, setViewAsOpen]   = useState(false); // the View as… dialog
  const { t: tAccount } = useTranslation('account');
  const { t: tViewAs } = useTranslation('viewAs');
  // The chat drawer (components/chat/ChatDrawer.js; ?chat=inbox | <channel>):
  // Back closes it, and focus goes back to the chat button -- on a phone,
  // whose menu closed behind it, to the menu button.
  const chatButtonRef = useRef(null);
  const menuButtonRef = useRef(null);
  const chat = useChatDrawer(user, { returnFocus: () => (phoneBar() ? menuButtonRef : chatButtonRef).current });
  const openChat = chat.offered ? chat.show : undefined;

  // ── The page swipe, Home -> Bio-Bank -> Experiments (D12, D24) ──
  // Read on the content of the pages in the chain only, by touch and pen
  // only (a mouse has the pager), and never while a dialog or sheet is open.
  // A physical left-to-right swipe goes on; right-to-left goes back.
  // utils/gestures/arbitrate.js leaves an isolate card's own swipe, the
  // system's edges and anything that scrolls sideways alone.
  const contentRef = useRef(null);
  const hubChain = useHubChain();
  const onChain = hubChain.index >= 0;
  useViewportGestures(contentRef, {
    enabled: onChain,
    swipes: ['right', 'left'],
    onSwipe: (direction) => hubChain.go(direction === 'right' ? 'next' : 'prev'),
    pointerTypes: ['touch', 'pen'],
    startsOn: (target, e) => !anyModalOpen()
      && swipeOwner(target, { x: e.clientX, width: window.innerWidth, root: contentRef.current }) === 'page',
    rules: PAGE_SWIPE,
  });
  // The page a swipe or the pager arrived at takes focus at its heading and
  // is announced, so a screen reader hears where it is.
  const [arrivedAt, setArrivedAt] = useState('');
  const tRef = useRef(t);
  tRef.current = t;
  useEffect(() => {
    const stop = takeArriving(location.pathname);
    if (!stop) return undefined;
    setArrivedAt(tRef.current('hubs:pager.arrived', { hub: tRef.current(stopLabel(stop)) }));
    // A lazily loaded page's heading may arrive a little after the address
    // changes: look for it a few times, briefly (a timer, not an animation
    // frame, which a hidden tab never runs).
    let timer = null;
    let tries = 0;
    const focusHeading = () => {
      const heading = contentRef.current?.querySelector('h1');
      if (!heading) {
        tries += 1;
        if (tries < 20) timer = setTimeout(focusHeading, 50);
        return;
      }
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    };
    timer = setTimeout(focusHeading, 0);
    return () => clearTimeout(timer);
  }, [location.pathname]);
  // An administrator may view the app as another role (read-only, an hour at
  // most); not offered while already viewing as one.
  const mayViewAs = !user?.vas && can(user, 'view_as:use');
  const accountRef = useRef(null);

  // Close nav + dropdowns on route change; the phone menu opens fresh next time.
  useEffect(() => {
    setMenuOpen(false); setLookupOpen(false); setAccountOpen(false); setOpenGroup(null);
    setPhoneOpen(new Set()); setPhoneQuery('');
  }, [location.pathname]);
  const togglePhoneHub = useCallback((key) => setPhoneOpen((open) => {
    const next = new Set(open);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  }), []);

  const closeAccount = useCallback(() => setAccountOpen(false), []);
  const closeGroup = useCallback(() => setOpenGroup(null), []);
  useDismiss(accountOpen, accountRef, closeAccount);

  const handleLookupResolved = (found) => {
    const { type, id } = found;
    setLookupOpen(false);
    if (type === 'collection') navigate('/collections', { state: { openCollectionId: id } });
    // An intake opens in the hub, over its isolates table.
    else if (type === 'external') navigate(intakeNav(id).path);
    else if (type === 'plate') navigate('/plates', { state: { openPlateId: id } });
    else if (type === 'slant') navigate('/slants', { state: { openSlantId: id } });
    else if (type === 'dna') navigate('/dna', { state: { openDnaId: id } });
    else if (type === 'dna_run') navigate('/dna', { state: { openRunId: id } });
    // Its own page, or its hub dossier when the reader sees it only redacted.
    else if (type === 'specimen') navigate(specimenNav(found).path);
  };

  // How many issue reports wait, beside their menu item: asked only when the
  // admin hub's menu (or its phone section) opens, never by the bar on its own.
  const triager = can(user, 'issue:read');
  const adminOpen = openGroup === 'admin' || (menuOpen && phoneOpen.has('admin'));
  useEffect(() => {
    if (!triager || !adminOpen) return undefined;
    let live = true;
    api.get('/issue-reports', { params: { status: 'open', limit: 0 } })
      .then((res) => { if (live && Number.isInteger(res?.data?.total)) setOpenIssues(res.data.total); })
      .catch(() => {});
    return () => { live = false; };
  }, [triager, adminOpen]);
  const extraFor = (item) => (item.to === '/admin/issues' && openIssues ? (
    <span data-testid="nav-open-issues" style={{ marginInlineStart: 8, fontSize: 12, fontWeight: 600, padding: '0 7px',
      borderRadius: 10, background: 'var(--c-danger-soft, var(--c-bg-muted))', color: 'var(--c-danger-fg, inherit)' }}>
      {t('openCount', { n: openIssues })}
    </span>
  ) : null);

  const hubs = visibleHubs(user);
  const currentHub = activeHubKey(location.pathname, user);
  const homeActive = location.pathname === '/';

  // A workspace is never a trap: one click gets you back to the chooser from
  // anywhere. Clearing the stored value first means the selector actually
  // asks, rather than bouncing straight back to the current mode.
  const switchWorkspace = () => {
    clearWorkspace();
    navigate('/workspace');
  };

  const toggleLanguage = (lng) => {
    if (lng !== i18n.language) i18n.changeLanguage(lng);
  };

  const languageSwitch = (inverse) => (
    <div className={`pn-lang${inverse ? ' is-inverse' : ''}`} role="group" aria-label="Language">
      <button type="button" className={i18n.language === 'en' ? 'is-on' : ''} title={t('tip.langEn')} onClick={() => toggleLanguage('en')}>EN</button>
      <button type="button" className={i18n.language === 'he' ? 'is-on' : ''} title={t('tip.langHe')} onClick={() => toggleLanguage('he')}>עב</button>
    </div>
  );

  return (
    <>
      {/* ── Top bar ── */}
      <nav className="pn-nav" aria-label={t('menu')}>
        <div className="pn-bar">

          {/* Brand + Home */}
          <div className="pn-start">
            <Link to="/" className="pn-brand">PITRIOT</Link>
            <Link to="/" className={`pn-home pn-desktop${homeActive ? ' is-active' : ''}`}
                  aria-current={homeActive ? 'page' : undefined}>
              {t('home')}
            </Link>
          </div>

          {/* One menu per hub this person may open (utils/hubs/registry.js) */}
          <div className="pn-groups pn-desktop">
            {hubs.map(hub => (
              <HubMenu
                key={hub.key}
                hub={hub}
                title={t(hub.label)}
                short={t(`hubs:${hub.key}.short`)}
                tip={t('hubs:tip.hubMenu', { hub: t(hub.label) })}
                active={currentHub === hub.key}
                open={openGroup === hub.key}
                onToggle={() => setOpenGroup(o => (o === hub.key ? null : hub.key))}
                onClose={closeGroup}
                t={t}
                extraFor={extraFor}
                pathname={location.pathname}
              />
            ))}
          </div>

          {/* Quick actions: search and profile. Last in the
              flex order, so they sit at the inline end -- the far left in Hebrew. */}
          <div className="pn-actions">
            {/* A metered account's AI credits (components/credits/AiCreditsBadge.js). */}
            <AiCreditsBadge user={user} as={IconButton} className="pn-desktop" />
            {/* Chat, with how many messages wait (components/chat/ChatButton.js). */}
            <ChatButton ref={chatButtonRef} user={user} label={t('quick.chat')} as={IconButton} className="pn-desktop"
              onChat={openChat} />
            {/* Report issue: on every page, for everyone signed in. */}
            <IconButton className="pn-desktop" label={t('quick.report')} onClick={openReport}
              aria-haspopup="dialog" data-testid="nav-report-issue">
              <FlagIcon />
            </IconButton>
            <IconButton className="pn-desktop" label={t('quick.search')} onClick={() => setLookupOpen(true)}>
              <SearchIcon />
            </IconButton>

            {/* Profile: username + account settings, language, theme, workspace,
                logout. Deleting the account is not in this menu: it lives in
                the Danger zone of /account, well away from log-out. */}
            <div ref={accountRef} className="pn-anchor pn-desktop">
              <IconButton
                label={user?.username ? `${t('quick.profile')}: ${user.username}` : t('quick.profile')}
                pressed={accountOpen}
                aria-haspopup="true"
                aria-expanded={accountOpen}
                onClick={() => setAccountOpen(o => !o)}
              >
                <UserIcon />
              </IconButton>

              {accountOpen && (
                <div className="pn-popover" style={{ width: 230, padding: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-fg)', padding: '2px 2px 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user?.username}
                  </div>
                  <Link to="/account" className="pn-account-link" title={tAccount('tip.settings')} onClick={closeAccount}>
                    <GearIcon />
                    <span>{tAccount('settings.title')}</span>
                  </Link>
                  <div style={{ marginBottom: 8 }}>{languageSwitch(false)}</div>
                  <div style={{ borderTop: '1px solid var(--c-border)', paddingTop: 6, marginBottom: 8 }}>
                    <ThemePicker />
                  </div>
                  {availableWorkspaces(user).length > 1 && (
                    <button type="button" onClick={switchWorkspace} title={t('tip.switchWorkspace')}
                      style={{ width: '100%', background: 'var(--c-surface)', color: 'var(--c-primary-fg)', border: '1px solid var(--c-primary)', borderRadius: 6, padding: '8px 0', cursor: 'pointer', fontSize: 13, marginBottom: 8 }}>
                      {tWorkspace('switcher.change')}
                    </button>
                  )}
                  {mayViewAs && (
                    <button type="button" onClick={() => { closeAccount(); setViewAsOpen(true); }}
                      title={tViewAs('openTip')} aria-haspopup="dialog" data-testid="open-view-as"
                      style={{ width: '100%', background: 'var(--c-surface)', color: 'var(--c-text-fg)', border: '1px solid var(--c-border)', borderRadius: 6, padding: '8px 0', cursor: 'pointer', fontSize: 13, marginBottom: 8 }}>
                      {tViewAs('open')}
                    </button>
                  )}
                  <button type="button" onClick={logout} title={t('tip.logout')}
                    style={{ width: '100%', background: 'var(--c-primary)', color: 'var(--c-on-accent)', border: 'none', borderRadius: 6, padding: '8px 0', cursor: 'pointer', fontSize: 13 }}>
                    {t('logout')}
                  </button>
                </div>
              )}
            </div>

            <QRLookupModal
              open={lookupOpen}
              onClose={() => setLookupOpen(false)}
              onResolved={handleLookupResolved}
            />
            {!sharedReport && <ReportIssueModal open={reportOpen} onClose={() => setReportOpen(false)} />}
            <ViewAsDialog open={viewAsOpen} onClose={() => setViewAsOpen(false)} />

            {/* Hamburger — narrow screens only */}
            <button
              ref={menuButtonRef}
              type="button"
              onClick={() => setMenuOpen(o => !o)}
              aria-label={t('menu')}
              title={t('tip.menu')}
              aria-expanded={menuOpen}
              className="pn-hamburger"
            >
              {menuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>

        {/* ── Mobile menu: a search, then the same hubs as collapsible sections ── */}
        {menuOpen && (
          <div className="pn-mobile">
            <div className="pn-mobile-head">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', minWidth: 0 }}>
                <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14, fontWeight: 500 }}>{user?.username}</span>
                <Link to="/account" className="pn-mobile-btn pn-mobile-account" title={tAccount('tip.settings')}
                      onClick={() => setMenuOpen(false)}>
                  <GearIcon />
                  <span>{tAccount('settings.title')}</span>
                </Link>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {languageSwitch(true)}
                <AiCreditsBadge user={user} as={IconButton} testId="ai-credits-badge-mobile" />
                <ChatButton user={user} label={t('quick.chat')} as={IconButton} onOpen={() => setMenuOpen(false)}
                  onChat={openChat} />
                <IconButton label={t('quick.report')} onClick={() => { setMenuOpen(false); openReport(); }}
                  aria-haspopup="dialog">
                  <FlagIcon />
                </IconButton>
                <IconButton label={t('quick.search')} onClick={() => { setMenuOpen(false); setLookupOpen(true); }}>
                  <SearchIcon />
                </IconButton>
                {mayViewAs && (
                  <button type="button" onClick={() => { setMenuOpen(false); setViewAsOpen(true); }}
                    className="pn-mobile-btn" title={tViewAs('openTip')} aria-haspopup="dialog">{tViewAs('open')}</button>
                )}
                <button type="button" onClick={logout} className="pn-mobile-btn" title={t('tip.logout')}>{t('logout')}</button>
              </div>
            </div>
            <div style={{ padding: '10px 20px', borderBottom: '1px solid rgba(255,255,255,0.15)', maxWidth: 320 }}>
              <ThemePicker inverse />
            </div>
            <PhoneHubs
              home={(
                <Link to="/" className={`pn-mobile-link${homeActive ? ' is-current' : ''}`}
                      aria-current={homeActive ? 'page' : undefined}>{t('home')}</Link>
              )}
              user={user}
              hubs={hubs}
              t={t}
              pathname={location.pathname}
              expanded={phoneOpen}
              onToggle={togglePhoneHub}
              query={phoneQuery}
              onQuery={setPhoneQuery}
              extraFor={extraFor}
            />
          </div>
        )}
      </nav>

      {/* ── Page content ──
          Moving to another section runs a hypha across the top padding, after
          the new page has painted; the page itself is never animated. The
          strip's pattern follows the section (components/organic/sectionMotif.js:
          roots for field work, septate hyphae for DNA and taxonomy, a spore for
          identification...). A change within a section -- a query, a deeper
          path, a state -- is not a transition, and the page is never remounted,
          so its state survives. Reduced motion: nothing at all. Sideways overflow is
          clipped, not hidden (.pn-content): `hidden` makes the div a scroll box of
          its own, and a page's sticky column would stick to it, never to the window. */}
      <div className="pn-content" ref={contentRef} data-swipe-page={onChain ? '' : undefined}
        style={{ padding: '16px', width: '100%', maxWidth: '100vw' }}>
        <HubPager />
        <span aria-live="polite" style={srOnly} data-testid="hub-arrived">{arrivedAt}</span>
        <SectionTransition pathname={location.pathname}>
          {/* A page opens a conversation in the drawer (chat/openChat.js). */}
          <ChatDrawerContext.Provider value={openChat || null}>{children}</ChatDrawerContext.Provider>
        </SectionTransition>
      </div>

      {/* DEV / STAGING / LOCAL at the foot of the page; nothing at all in production. */}
      <EnvironmentBadge variant="footer" />

      {/* The chat, from the left over any page; outside the bar and the page,
          so neither's stacking or headings reach it. */}
      <ChatDrawer user={user} chat={chat} />

      {/* ── Navbar CSS ──
          Logical properties throughout (inset-inline-*, margin-inline-*), so the
          same rules lay out left-to-right in English and right-to-left in Hebrew.
          `nowrap` on the bar and every trigger is the actual fix for the old bar:
          nothing in it may wrap onto a second line or spill under the icons. */}
      <style>{`
        .pn-content { overflow-x: hidden; overflow-x: clip; }
        /* The pages in the swipe chain hand vertical scrolling and pinch to the
           browser and keep sideways strokes for the page swipe (a table that
           scrolls sideways is its own scroll box, and keeps its own). */
        @media (pointer: coarse) { .pn-content[data-swipe-page] { touch-action: pan-y pinch-zoom; } }
        .hub-pager { display: flex; align-items: center; justify-content: space-between; gap: 8px;
          margin: 0 0 12px; min-height: 44px; }
        .hub-pager-step { display: inline-flex; align-items: center; gap: 6px; min-height: 44px; min-width: 44px;
          padding: 0 12px; border: 1px solid var(--c-border); border-radius: 22px; background: var(--c-surface);
          color: var(--c-text-fg); font: inherit; font-size: 14px; cursor: pointer; max-width: 40vw;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .hub-pager-step:hover { background: var(--c-primary-soft-3, var(--c-surface)); }
        .hub-pager-step:focus-visible, .hub-pager-dot:focus-visible { outline: 2px solid var(--c-primary); outline-offset: 2px; }
        .hub-pager-step--none { visibility: hidden; border: 0; }
        .hub-pager-chevron { font-size: 20px; line-height: 1; }
        .hub-pager-dots { display: flex; gap: 4px; list-style: none; margin: 0; padding: 0; }
        .hub-pager-dot { display: flex; align-items: center; justify-content: center; width: 24px; height: 44px; }
        .hub-pager-dot::before { content: ''; width: 10px; height: 10px; border-radius: 50%;
          border: 2px solid var(--c-primary); box-sizing: border-box; }
        .hub-pager-dot[aria-current="page"]::before { background: var(--c-primary); }
        .pn-nav { background-color: var(--c-primary); position: relative; z-index: 300; }
        .pn-bar {
          display: flex; flex-wrap: nowrap; white-space: nowrap; align-items: center;
          justify-content: space-between; gap: 12px; padding: 0 16px; height: 52px; min-width: 0;
        }
        .pn-start { display: flex; flex-wrap: nowrap; align-items: center; gap: 10px; flex-shrink: 0; }
        .pn-brand { color: var(--c-on-accent); font-weight: 800; font-size: 17px; letter-spacing: 1px; text-decoration: none; }
        .pn-home, .pn-group-trigger {
          color: rgba(255,255,255,0.82); background: none; border: none; border-radius: 6px;
          padding: 6px 10px; font-size: 14px; font-family: inherit; cursor: pointer; text-decoration: none;
          white-space: nowrap; display: inline-flex; align-items: center; gap: 6px; line-height: 1.2;
        }
        .pn-home:hover, .pn-group-trigger:hover { background: rgba(255,255,255,0.12); color: var(--c-on-accent); }
        .pn-home.is-active, .pn-group-trigger.is-active { color: var(--c-on-accent); font-weight: 600; background: rgba(255,255,255,0.15); }
        .pn-group-trigger.is-open { background: rgba(255,255,255,0.22); color: var(--c-on-accent); }
        .pn-group-trigger svg { transition: transform .15s ease; }
        .pn-group-trigger.is-open svg { transform: rotate(180deg); }
        .pn-groups { display: flex; flex-wrap: nowrap; white-space: nowrap; align-items: center; gap: 4px; flex: 1 1 auto; min-width: 0; }
        .pn-group, .pn-anchor { position: relative; }
        .pn-menu, .pn-popover {
          position: absolute; top: calc(100% + 6px); background: var(--c-surface); border-radius: 8px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.18); border: 1px solid var(--c-border); z-index: 400; white-space: normal;
        }
        .pn-menu {
          inset-inline-start: 0; min-width: 230px; padding: 6px; display: flex; flex-direction: column;
          max-height: calc(100vh - 70px); overflow-y: auto;
        }
        .pn-menu-hub { font-weight: 600; color: var(--c-primary-fg); border-bottom: 1px solid var(--c-border); border-radius: 6px 6px 0 0; margin-bottom: 2px; }
        .pn-menu-section { display: flex; flex-direction: column; }
        .pn-menu-heading {
          padding: 8px 12px 2px; font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase;
          color: var(--c-text-muted-fg);
        }
        .pn-popover { inset-inline-end: 0; }
        .pn-menu-item {
          display: block; padding: 9px 12px; border-radius: 6px; color: var(--c-text-fg); text-decoration: none;
          font-size: 14px; white-space: nowrap;
        }
        .pn-menu-item:hover, .pn-menu-item:focus-visible { background: var(--c-bg-subtle); outline: none; }
        .pn-menu-item.is-current { background: var(--c-bg-muted); font-weight: 600; }
        .pn-actions { display: flex; flex-wrap: nowrap; align-items: center; gap: 6px; flex-shrink: 0; }
        .pn-icon {
          position: relative; display: inline-flex; align-items: center; justify-content: center;
          width: 34px; height: 34px; border: none; border-radius: 8px; cursor: pointer;
          background: rgba(255,255,255,0.14); color: var(--c-on-accent);
        }
        .pn-icon:hover, .pn-icon.is-open { background: rgba(255,255,255,0.26); }
        .pn-icon:focus-visible, .pn-group-trigger:focus-visible, .pn-home:focus-visible {
          outline: 2px solid var(--c-on-accent); outline-offset: 2px;
        }
        .pn-icon[data-tooltip]::after {
          content: attr(data-tooltip); position: absolute; top: calc(100% + 8px); left: 50%;
          transform: translateX(-50%); background: rgba(17,24,39,0.94); color: #fff; font-size: 12px;
          font-weight: 500; padding: 4px 8px; border-radius: 5px; white-space: nowrap; pointer-events: none;
          opacity: 0; transition: opacity .12s ease .25s; z-index: 500;
        }
        .pn-icon:hover::after, .pn-icon:focus-visible::after { opacity: 1; }
        .pn-icon.is-open::after { display: none; }
        .pn-lang { display: flex; border: 1px solid var(--c-border); border-radius: 6px; overflow: hidden; }
        .pn-lang button {
          flex: 1; background: var(--c-surface); color: var(--c-text-fg); border: none; padding: 6px 10px;
          font-size: 12px; font-weight: 600; cursor: pointer;
        }
        .pn-lang button.is-on { background: var(--c-bg-muted); }
        .pn-lang.is-inverse { border-color: rgba(255,255,255,0.35); }
        .pn-lang.is-inverse button { background: none; color: var(--c-on-accent); }
        .pn-lang.is-inverse button.is-on { background: rgba(255,255,255,0.25); }
        .pn-hamburger {
          display: none; background: none; border: 1px solid rgba(255,255,255,0.35); color: var(--c-on-accent);
          border-radius: 6px; padding: 4px 8px; cursor: pointer; font-size: 20px; line-height: 1;
        }
        .pn-mobile {
          position: absolute; top: 100%; left: 0; right: 0; max-height: calc(100vh - 52px); overflow-y: auto;
          background: var(--c-primary-deep); border-top: 1px solid rgba(255,255,255,0.1); box-shadow: 0 8px 24px rgba(0,0,0,0.3);
        }
        .pn-mobile-head {
          display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px;
          padding: 14px 20px; border-bottom: 1px solid rgba(255,255,255,0.15);
        }
        .pn-mobile-btn {
          background: rgba(255,255,255,0.15); color: var(--c-on-accent); border: none; border-radius: 6px;
          padding: 5px 12px; cursor: pointer; font-size: 13px; white-space: nowrap;
        }
        .pn-mobile-account { display: inline-flex; align-items: center; gap: 6px; text-decoration: none; }
        .pn-account-link {
          display: flex; align-items: center; gap: 8px; margin-bottom: 8px; padding: 8px 10px; border-radius: 6px;
          border: 1px solid var(--c-border); color: var(--c-primary-fg); text-decoration: none;
          font-size: 13px; font-weight: 500;
        }
        .pn-account-link:hover { background: var(--c-bg-subtle); }
        .pn-account-link:focus-visible { outline: 2px solid var(--c-primary); outline-offset: 1px; }
        .pn-account-link svg, .pn-mobile-account svg { flex-shrink: 0; }
        @media (pointer: coarse) {
          .pn-account-link, .pn-mobile-account { min-height: 44px; }
        }
        .pn-mobile-title {
          padding: 12px 20px 4px; font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase;
          color: rgba(255,255,255,0.55);
        }
        .pn-mobile-link {
          display: block; color: rgba(255,255,255,0.85); text-decoration: none; padding: 12px 20px 12px 28px;
          padding-inline-start: 28px; font-size: 15px; border-bottom: 1px solid rgba(255,255,255,0.07);
        }
        .pn-mobile-link.is-current { color: var(--c-on-accent); font-weight: 600; background: rgba(255,255,255,0.1); }
        .pn-mobile-search { display: flex; flex-direction: column; gap: 4px; padding: 4px 20px 12px; border-bottom: 1px solid rgba(255,255,255,0.15); }
        .pn-mobile-search .pn-mobile-title { padding: 8px 0 0; }
        .pn-mobile-search input {
          width: 100%; max-width: 420px; padding: 8px 10px; border-radius: 6px; font-size: 15px;
          border: 1px solid rgba(255,255,255,0.35); background: var(--c-surface); color: var(--c-text-fg);
        }
        .pn-mobile-search input:focus-visible { outline: 2px solid var(--c-on-accent); outline-offset: 1px; }
        .pn-accordion {
          display: flex; align-items: center; justify-content: space-between; gap: 10px; width: 100%;
          padding: 14px 20px; background: none; border: none; border-bottom: 1px solid rgba(255,255,255,0.1);
          color: var(--c-on-accent); font-size: 15px; font-weight: 600; font-family: inherit; text-align: start; cursor: pointer;
        }
        .pn-accordion:focus-visible { outline: 2px solid var(--c-on-accent); outline-offset: -2px; }
        .pn-accordion svg { flex-shrink: 0; transition: transform .15s ease; }
        .pn-accordion[aria-expanded="true"] svg { transform: rotate(180deg); }
        .pn-mobile-hublink { font-weight: 600; }
        .pn-mobile-hubname { display: block; font-size: 12px; color: rgba(255,255,255,0.6); margin-top: 2px; }
        .pn-mobile-none { padding: 12px 20px; margin: 0; color: rgba(255,255,255,0.75); font-size: 14px; }
        @media (max-width: 900px) {
          .pn-desktop { display: none !important; }
          .pn-hamburger { display: block; }
        }
        @media (prefers-reduced-motion: reduce) {
          .pn-group-trigger svg, .pn-accordion svg, .pn-icon[data-tooltip]::after { transition: none; }
        }
      `}</style>
    </>
  );
}
